/**
 * Minecraft-Chattext mit &-Codes (&6 Gold, &l fett …) in JSON-Textbausteine für
 * tellraw/title umwandeln. JSON.stringify übernimmt das Maskieren – vorher wurden
 * Anführungszeichen doppelt maskiert und erschienen im Spiel als \".
 */

const COLOR_NAMES: Record<string, string> = {
  '0': 'black', '1': 'dark_blue', '2': 'dark_green', '3': 'dark_aqua',
  '4': 'dark_red', '5': 'dark_purple', '6': 'gold', '7': 'gray',
  '8': 'dark_gray', '9': 'blue', a: 'green', b: 'aqua',
  c: 'red', d: 'light_purple', e: 'yellow', f: 'white',
};

const FORMATS: Record<string, 'bold' | 'italic' | 'underlined' | 'strikethrough' | 'obfuscated'> = {
  l: 'bold', o: 'italic', n: 'underlined', m: 'strikethrough', k: 'obfuscated',
};

export interface McComponent {
  text: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underlined?: boolean;
  strikethrough?: boolean;
  obfuscated?: boolean;
}

/** Wie im Spiel: eine Farbe setzt die Formatierung zurück, &r stellt die Grundfarbe wieder her. */
export const mcTextToComponents = (text: string, baseColor = 'white'): McComponent[] => {
  const out: McComponent[] = [];
  let style: Omit<McComponent, 'text'> = { color: baseColor };
  let buf = '';
  const flush = () => {
    if (buf) out.push({ text: buf, ...style });
    buf = '';
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    const code = (text[i + 1] || '').toLowerCase();
    if ((ch === '&' || ch === '§') && code) {
      if (COLOR_NAMES[code]) { flush(); style = { color: COLOR_NAMES[code] }; i++; continue; }
      if (FORMATS[code]) { flush(); style = { ...style, [FORMATS[code]!]: true }; i++; continue; }
      if (code === 'r') { flush(); style = { color: baseColor }; i++; continue; }
    }
    buf += ch;
  }
  flush();
  return out;
};

/** Chatnachricht an alle: „[Absender] Text" – Absender gold/fett, Text standardmäßig gelb. */
export const tellrawCommand = (message: string, sender = '', baseColor = 'yellow') => {
  const extra: McComponent[] = [];
  if (sender.trim()) extra.push({ text: `[${sender.trim()}] `, color: 'gold', bold: true });
  extra.push(...mcTextToComponents(message, baseColor));
  return `tellraw @a ${JSON.stringify({ text: '', extra })}`;
};

// Bedrock versteht kein Java-JSON: dort rawtext mit §-Codes im Text.
const toSection = (s: string) => s.replace(/&([0-9a-fk-or])/gi, '§$1');

export const bedrockTellrawCommand = (message: string, sender = '') =>
  `tellraw @a ${JSON.stringify({ rawtext: [{ text: `${sender.trim() ? `§6§l[${sender.trim()}] §r` : ''}§e${toSection(message)}` }] })}`;

export const bedrockTitleCommands = (message: string, sender = ''): string[] => {
  const body = JSON.stringify({ rawtext: [{ text: `§e${toSection(message)}` }] });
  if (!sender.trim()) return [`titleraw @a title ${body}`];
  return [`titleraw @a subtitle ${body}`, `titleraw @a title ${JSON.stringify({ rawtext: [{ text: `§6§l${sender.trim()}` }] })}`];
};

/** Großer Bildschirmtext: Absender als Titel, Nachricht als Untertitel (Untertitel zuerst senden). */
export const titleCommands = (message: string, sender = '', baseColor = 'yellow'): string[] => {
  const body = JSON.stringify({ text: '', extra: mcTextToComponents(message, baseColor) });
  if (!sender.trim()) return [`title @a title ${body}`];
  return [
    `title @a subtitle ${body}`,
    `title @a title ${JSON.stringify({ text: sender.trim(), color: 'gold', bold: true })}`,
  ];
};
